import { ContentContainer } from "components/ui/display/ContentContainer";
import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { parseDateToTimestamp } from "utilities/dateAndTime";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";


export const NotesGridCard = ({note, onEdit, onArchive}) => {

    const onDoubleClickHandler = () => {
        const target = '/doc/' + note.noteUuid;
        window.open(target, "_blank", 'noopener,noreferer');
    }

    const titleRef = useRef(null);
    const [availableLines, setAvailableLines] = useState(3);

    useEffect(() => {
        const el = titleRef.current;
        if(!el) return;

        let rafId = null;

        const calculate = () => {
            const style = window.getComputedStyle(el);
            const lineHeight = parseFloat(style.lineHeight);

            if (!lineHeight || Number.isNaN(lineHeight)) return;

            const height = el.offsetHeight;
            const rawLines = Math.floor((height + 0.5) / lineHeight);
            const titleLines = Math.max(1, Math.min(3, rawLines));
            const nextSummaryLines = Math.max(1, 5 - titleLines);
            setAvailableLines(prev =>
                prev === nextSummaryLines ? prev : nextSummaryLines
            );
        };

        const schedule = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(calculate);
        };

        schedule();

        const observer = new ResizeObserver(schedule);
        observer.observe(el);

        if (document.fonts?.ready) {
            document.fonts.ready.then(schedule);
        }

        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };


    }, [note.noteTitle])

    return (
        <StyledA
            href={'/doc/' + note.noteUuid}
            target="_blank"
        >
            <StyledNotesGridCard padding='md' dropShadow='sm' onDoubleClick={onDoubleClickHandler}>
                <div className="cardHeader">
                    <div className="iconContainer">
                        <MaterialIcon
                            name='draft'
                            weight={300}
                            size="lg"
                        />
                    </div>
                </div>
                <div className="cardContent">
                    <div ref={titleRef} className="cardTitle">
                        {note.noteTitle}
                    </div>
                    <div
                        className="cardSummary"
                        style={{
                            display: "-webkit-box",
                            WebkitLineClamp: availableLines,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {note.noteContentSummary}
                    </div>
                </div>
                <div className="cardFooter">
                    <div className="cardLastEdit">
                        <MaterialIcon
                            name='calendar_today'
                            weight={300}
                            size='sm'
                        />
                        {parseDateToTimestamp(new Date(note.noteTimestamp))}
                    </div>
                    <div className="cardOptions">
                        <div className="moreIcon">
                            <MaterialIcon
                                name='more_vert'
                                size='sm'
                            />
                        </div>
                        <div className="moreOptions">
                            <MaterialIcon
                                name="inventory_2"
                                weight={300}
                                size='sm'
                                color={({theme}) => theme.colors.errorForeground}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onArchive()
                                }}
                            />
                            <MaterialIcon
                                name='edit'
                                weight={300}
                                size='sm'
                                color={({theme}) => theme.colors.mutedForeground}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onEdit()
                                }}
                            />
                        </div>
                    </div>
                </div>
            </StyledNotesGridCard>
        </StyledA>
    )
}

const StyledA = styled.a`
    display: block;
    text-decoration: none;
`

const StyledNotesGridCard = styled(ContentContainer)`
    cursor: pointer;
    height: 225px;

    div.cardHeader
    {
        padding: ${({theme}) => theme.spacing.md};

        div.iconContainer
        {
            width: 38px;
            height: 38px;
            background: ${({theme}) => theme.colors.neutralSecondaryForeground};
            border-radius: ${({theme}) => theme.cornerRadius.sm};
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${({theme}) => theme.colors.neutralPrimaryBackground};
        }
    }

    div.cardContent
    {
        padding: 0 ${({theme}) => theme.spacing.md};
        margin-bottom: ${({theme}) => theme.spacing.md};
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: ${({theme}) => theme.spacing.sm};
        overflow: hidden;

        div.cardTitle
        {
            font-weight: 600;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;  
            overflow: hidden;
            line-height: 1.25rem;
            color: ${({theme}) => theme.colors.neutralSecondaryForeground};

        }

        div.cardSummary
        {
            color: ${({theme}) => theme.colors.mutedForeground};
            display: -webkit-box;
            //-webkit-line-clamp: 3;
            -webkit-box-orient: vertical;  
            overflow: hidden;
            line-height: 1.25rem;
        }
    }

    div.cardFooter
    {
        border-top: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        padding: ${({theme}) => theme.spacing.md} ${({theme}) => theme.spacing.md};
        padding-bottom: 0;
        display: flex;
        justify-content: space-between;

        div.cardLastEdit
        {
            display: flex;
            align-items: center;
            gap: ${({theme}) => theme.spacing.sm};
            color: ${({theme}) => theme.colors.mutedForeground};
        }

        div.cardOptions
        {
            display: flex;

            div.moreIcon
            {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                transition-duration: 110ms;
                color: ${({theme}) => theme.colors.mutedForeground}
            }

            div.moreOptions
            {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: ${({theme}) => theme.spacing.sm};
                overflow: hidden;
                width: 0;
                transition-duration: 110ms;
            }

            &:hover
            {
                div.moreIcon
                {
                    width: 0;
                    opacity: 0;
                }
            
                div.moreOptions
                {
                    width: 47px;
                }
            }
        }
    }
`